import { Component, OnInit, NgZone, ChangeDetectorRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { environment } from '../../../environments/environment';

// Import Thai font
import { SarabunNormal, SarabunBold } from '../../services/fonts/sarabun-font';

export interface SlipData {
  receiptNumber: string;
  date: string;
  paymentType: string;
  items: {
    no: number;
    description: string;
    quantity: number;
    pricePerUnit: number;
    amount: number;
  }[];
  subtotal: number;
  vat: number;
  total: number;
  paymentMethod: string;
  payer: {
    name: string;
    address: string;
    taxId?: string;
    /**
     * ชื่อ-นามสกุลของสมาชิกที่ใช้บริการ ใส่ไว้เฉพาะกรณีออกใบเสร็จในนามนิติบุคคล
     * เพราะช่อง "ชื่อ" จะเป็นชื่อองค์กร ทำให้ไม่ทราบว่าเป็นใบเสร็จของใคร
     */
    serviceRecipient?: string;
  };
  notes?: string;
}

@Component({
  selector: 'app-renew-slip',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './renew-slip.component.html',
  styleUrl: './renew-slip.component.scss'
})
export class RenewSlipComponent implements OnInit {
  slipData: SlipData | null = null;
  isLoading = false;
  error: string | null = null;
  paymentId: string | null = null;
  tabType: string | null = null;
  rawApiData: any = null; // Store raw API data to access originalReceipt
  userType: string | null = null; // 'user' or 'staff'
  isOriginalReceipt: boolean = true; // Track if this is original or copy

  // The specific receipt_log row that was clicked in the worklist.
  // Its codeReceipt, payer snapshot, and type are authoritative.
  receiptLogId: string | null = null;
  receiptLogData: any = null;

  /** หน้าที่กดเข้ามาดูใบเสร็จ (ใช้ตอนกดปุ่ม "กลับ") */
  returnUrl: string | null = null;

  /** เข้ามาหน้านี้จากหน้าอื่นในระบบหรือไม่ (ไม่ใช่เปิดลิงก์ตรงๆ) */
  private cameFromInsideApp = false;

  /** ข้อมูลที่แสดงเป็นใบเสร็จตัวอย่าง (ไม่ใช่เอกสารจริง) */
  isSampleData = false;

  /** ลองค้นหา receipt log จากรหัสใบสมัครสอบไปแล้วหรือยัง (กันวนซ้ำ) */
  private examLogRetryDone = false;

  // Staff receipt actions
  isActionLoading = false;
  actionError: string | null = null;
  actionSuccess: string | null = null;
  showCancelForm = false;
  cancelReasonInput = '';

  get isFormType(): boolean {
    return ['member-card', 'renew-member', 'register-appraiser', 'appraiser-renewal', 'renew-appraiser', 'training'].includes(this.tabType || '');
  }

  /** หน้านี้จัดการใบเสร็จได้หรือไม่ (ฟอร์มคำขอ + การสมัครสอบ) */
  get isReceiptManageable(): boolean {
    return this.isFormType || this.tabType === 'exam';
  }

  /** รหัสใบสมัครสอบของใบเสร็จนี้ (ใช้ตอนยกเลิก/ออกใบเสร็จใหม่) */
  private get examListId(): string | null {
    return this.rawApiData?.examListId || this.rawApiData?.examList?.id || null;
  }

  get hasActiveReceipt(): boolean {
    // การสมัครสอบ: ข้อมูลที่โหลดมาคือ receipt log จึงดูจาก type ของตัวมันเอง
    if (this.tabType === 'exam') {
      return (this.receiptLogData?.type || this.rawApiData?.type) === 'created';
    }

    if (this.receiptLogData) return this.receiptLogData.type === 'created';
    return !!(this.rawApiData?.codeReceipt);
  }

  /**
   * ใบเสร็จถูกยกเลิก = เคยออกใบเสร็จแล้วและถูกยกเลิกภายหลัง
   * (ยกเลิกแล้ว backend จะล้าง codeReceipt และบันทึก cancelReason ไว้)
   */
  get isReceiptCancelled(): boolean {
    if (this.tabType === 'exam') {
      const type = this.receiptLogData?.type || this.rawApiData?.type;
      return type === 'canceled' || type === 'cancelled';
    }

    if (this.receiptLogData) {
      return this.receiptLogData.type === 'canceled' || this.receiptLogData.type === 'cancelled';
    }

    if (this.rawApiData?.codeReceipt) return false;

    return !!this.rawApiData?.cancelReason
      || this.rawApiData?.paymentStatus === 'ยกเลิกการชำระ';
  }

  /**
   * ยังไม่เคยออกใบเสร็จสำหรับรายการนี้ (ไม่ใช่กรณีถูกยกเลิก)
   */
  get hasNoReceiptYet(): boolean {
    return !this.hasActiveReceipt && !this.isReceiptCancelled;
  }

  private getFormApiBase(): string | null {
    switch (this.tabType) {
      case 'member-card': return 'form-request-new-member';
      case 'renew-member': return 'form-renew-member';
      case 'register-appraiser': return 'form-regis-appraiser';
      case 'appraiser-renewal':
      case 'renew-appraiser': return 'form-renew-appraiser';
      case 'training': return 'cpd-hours';
      default: return null;
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private location: Location
  ) { }

  ngOnInit(): void {
    // Get payment ID from route params
    this.paymentId = this.route.snapshot.paramMap.get('id');

    // Get tab type and receipt log reference from query params
    this.tabType = this.route.snapshot.queryParamMap.get('type');
    this.receiptLogId = this.route.snapshot.queryParamMap.get('receiptLogId');
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    // มีการนำทางภายในแอปมาก่อนหน้านี้หรือไม่ (ถ้าเปิดลิงก์ตรงๆ จะไม่มี)
    this.cameFromInsideApp = !!this.router.lastSuccessfulNavigation?.previousNavigation;

    // Get user type from localStorage
    const currentUserString = localStorage.getItem('currentUser');
    if (currentUserString) {
      try {
        const currentUser = JSON.parse(currentUserString);
        this.userType = currentUser.userType || 'user';
        console.log('📦 Current User from localStorage:', currentUser);
      } catch (e) {
        console.warn('Failed to parse currentUser from localStorage');
        this.userType = 'user';
      }
    } else {
      console.warn('No currentUser found in localStorage');
      this.userType = 'user';
    }

    console.log('🔍 Renew Slip - Payment ID:', this.paymentId);
    console.log('🔍 Renew Slip - Tab Type:', this.tabType);
    console.log('👤 User Type:', this.userType);

    // Load data from API
    if (this.paymentId) {
      this.loadPaymentData(this.paymentId);
    } else {
      // Use sample data only if no payment ID
      this.loadSampleData();
    }
  }

  /**
   * Load payment data from API
   */
  loadPaymentData(paymentId: string): void {
    console.log('🚀 Starting loadPaymentData...');
    console.log('📋 Payment ID:', paymentId);
    console.log('📋 Tab Type:', this.tabType);

    this.error = null;

    // Determine API endpoint based on tab type
    let apiUrl: string;

    switch (this.tabType) {
      case 'member-card':
        apiUrl = `${environment.apiUrl}/form-request-new-member/${paymentId}`;
        break;
      case 'renew-member':
        apiUrl = `${environment.apiUrl}/form-renew-member/${paymentId}`;
        break;
      case 'register-appraiser':
        apiUrl = `${environment.apiUrl}/form-regis-appraiser/${paymentId}`;
        break;
      case 'appraiser-renewal':
      case 'renew-appraiser':
        apiUrl = `${environment.apiUrl}/form-renew-appraiser/${paymentId}`;
        break;
      case 'training':
        apiUrl = `${environment.apiUrl}/cpd-hours/${paymentId}`;
        break;
      case 'exam':
        apiUrl = `${environment.apiUrl}/receipt-log/${paymentId}`;
        break;
      case 'payment':
        apiUrl = `${environment.apiUrl}/payments/${paymentId}`;
        break;
      default:
        // ประเภทใบเสร็จไม่ถูกต้อง → ไม่ดึงข้อมูลมั่ว และไม่แสดงใบเสร็จตัวอย่าง
        console.error('❌ Unknown receipt type:', this.tabType);
        this.slipData = null;
        this.isSampleData = false;
        this.error = `ไม่สามารถออกใบเสร็จได้: ไม่รู้จักประเภทของรายการ (${this.tabType || '-'})`;
        this.isLoading = false;
        this.cdr.detectChanges();
        return;
    }

    console.log('📡 API URL:', apiUrl);

    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {
        console.log('✅ SUCCESS - Response received:', response);

        this.ngZone.run(() => {
          // Store raw API data
          this.rawApiData = response;

          // For exam, originalReceipt lives inside the nested examList
          if (this.tabType === 'exam') {
            const examList = response.examList;
            this.isOriginalReceipt = examList?.originalReceipt !== undefined ? examList.originalReceipt : true;
          } else {
            // Check originalReceipt status (handle both 'originalReceipt' and 'orginalReceipt' typo)
            this.isOriginalReceipt = response.originalReceipt !== undefined
              ? response.originalReceipt
              : (response.orginalReceipt !== undefined ? response.orginalReceipt : true);
          }

          console.log('📄 Original Receipt Status:', this.isOriginalReceipt);

          if (!response) {
            console.warn('⚠️ Response is null or undefined');
            this.slipData = null;
            this.isSampleData = false;
            this.error = 'ไม่พบข้อมูลใบเสร็จของรายการนี้';
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          try {
            console.log('🔄 Mapping API response to slip data...');
            const mappingData = this.tabType === 'exam' ? this.normalizeExamResponse(response) : response;
            this.slipData = this.mapApiResponseToSlipData(mappingData);
            this.isSampleData = false;
            console.log('✅ Slip data created:', this.slipData);
            this.isLoading = false;
            this.error = null;
            this.cdr.detectChanges();

            // For form types, also load the specific receipt_log so the
            // receipt number (เลขที่) and payer snapshot come from that
            // exact log row — not from the form entity whose codeReceipt
            // always reflects the current active receipt, not a historical one.
            if (this.receiptLogId && this.isFormType) {
              this.http.get<any>(`${environment.apiUrl}/receipt-log/${this.receiptLogId}`).subscribe({
                next: (log) => {
                  this.ngZone.run(() => {
                    this.receiptLogData = log;
                    if (this.slipData) {
                      if (log.codeReceipt) {
                        this.slipData.receiptNumber = log.codeReceipt;
                      }
                      if (log.payerName || log.payerAddress || log.payerTaxId) {
                        this.slipData.payer = {
                          name: log.payerName || this.slipData.payer.name,
                          address: log.payerAddress || this.slipData.payer.address,
                          taxId: log.payerTaxId || this.slipData.payer.taxId,
                          // ชื่อสมาชิกมาจาก user ของคำขอ ไม่ได้อยู่ใน snapshot จึงต้องคงไว้
                          // และ receipt_log เองก็บอกได้ว่าใบนี้ออกในนามนิติบุคคล (usedFor)
                          serviceRecipient: this.slipData.payer.serviceRecipient
                            || (this.isCompanyReceipt(log) ? this.getServiceRecipientName(this.rawApiData) : undefined),
                        };
                      }
                    }
                    this.cdr.detectChanges();
                  });
                },
                error: () => { /* form entity data already shown — silently ignore */ }
              });
            }
          } catch (mappingError) {
            console.error('❌ Mapping error:', mappingError);
            this.slipData = null;
            this.isSampleData = false;
            this.error = 'เกิดข้อผิดพลาดในการประมวลผลข้อมูลใบเสร็จ กรุณาติดต่อเจ้าหน้าที่';
            this.isLoading = false;
            this.cdr.detectChanges();
          }
        });
      },
      error: (error) => {
        console.error('❌ ERROR - HTTP Error:', error);
        console.error('❌ Status:', error.status);
        console.error('❌ Message:', error.message);

        this.ngZone.run(() => {
          // Handle 304 Not Modified
          if (error.status === 304 && error.error) {
            console.log('ℹ️ 304 Not Modified - extracting data from error.error');
            try {
              const mappingData304 = this.tabType === 'exam' ? this.normalizeExamResponse(error.error) : error.error;
              this.slipData = this.mapApiResponseToSlipData(mappingData304);
              console.log('✅ Slip data from 304:', this.slipData);
              this.isLoading = false;
              this.error = null;
              this.cdr.detectChanges();
              return;
            } catch (mappingError) {
              console.error('❌ Failed to map 304 data:', mappingError);
            }
          }

          // สมัครสอบ: อาจได้รับรหัสใบสมัคร (exam list) มาแทนรหัส receipt log
          // ลองหา receipt log ของใบสมัครนั้นให้อีกครั้งก่อนแจ้ง error
          if (this.tabType === 'exam' && !this.examLogRetryDone) {
            this.examLogRetryDone = true;
            this.retryExamReceiptByExamList(paymentId);
            return;
          }

          // ไม่แสดงใบเสร็จตัวอย่างแทนข้อมูลจริง เพราะผู้ใช้อาจเข้าใจผิดว่าเป็นใบเสร็จจริง
          this.slipData = null;
          this.isSampleData = false;
          this.error = `ไม่สามารถโหลดข้อมูลใบเสร็จได้: ${error.message}`;
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      },
      complete: () => {
        console.log('✅ HTTP request completed');
        this.ngZone.run(() => {
          if (this.isLoading) {
            console.warn('⚠️ Still loading after complete - forcing false');
            this.isLoading = false;
          }
        });
      }
    });
  }

  /**
   * Flatten a receipt-log response for the exam type so the shared mapping
   * functions can work with it unchanged. The receipt-log's codeReceipt, amount,
   * and transferDateTime take precedence over the nested examList copies.
   */
  private normalizeExamResponse(receiptLog: any): any {
    const examList = receiptLog.examList || {};
    return {
      ...examList,
      codeReceipt: receiptLog.codeReceipt || examList.codeReceipt,
      amount: receiptLog.amount || examList.amount,
      transferDateTime: receiptLog.transferDateTime || examList.transferDateTime,
      // Payer snapshot is stored on the receipt-log row, not the nested examList.
      payerName: receiptLog.payerName ?? examList.payerName,
      payerAddress: receiptLog.payerAddress ?? examList.payerAddress,
      payerTaxId: receiptLog.payerTaxId ?? examList.payerTaxId,
    };
  }

  /**
   * Helper function to build full address from components
   * Checks both currentAddress and address fields for flexibility
   */
  private buildFullAddress(userInfo: any): string {
    const parts: string[] = [];
    console.log('Building address from:', userInfo);

    // Try currentAddress first, then fall back to address
    const address = userInfo?.address;
    const subdistrict = userInfo?.subdistrict;
    const district = userInfo?.district;
    const province = userInfo?.province;
    const postalCode = userInfo?.postalCode;

    if (address) parts.push(address);
    if (subdistrict) parts.push(`ตำบล ${subdistrict}`);
    if (district) parts.push(`อำเภอ ${district}`);
    if (province) parts.push(`จังหวัด ${province}`);
    if (postalCode) parts.push(postalCode);

    return parts.length > 0 ? parts.join(' ') : '-';
  }

  /**
   * Get payer information based on receiptType
   * If receiptType is 'company', use company information
   * If receiptType is 'individual', use user information
   */
  private getPayerInfo(apiData: any): { name: string; address: string; taxId: string; serviceRecipient?: string } {
    const receiptType = apiData.typeReceipt || apiData.receiptType;
    const isCompanyReceipt = this.isCompanyReceipt(apiData);

    // Prefer the payer snapshot frozen on the record at receipt creation time.
    // Falls back to live resolution below for receipts issued before snapshots existed.
    if (apiData.payerName || apiData.payerAddress || apiData.payerTaxId) {
      console.log('🧾 Using stored payer snapshot');
      return {
        name: apiData.payerName || '-',
        address: apiData.payerAddress || '-',
        taxId: apiData.payerTaxId || '',
        serviceRecipient: isCompanyReceipt ? this.getServiceRecipientName(apiData) : undefined
      };
    }

    console.log('📋 Receipt Type:', receiptType);

    if (receiptType === 'company') {
      // Use company information from user.userInformation.company
      const company = apiData.user?.userInformation?.company;
      console.log('🏢 Using company information:', company);

      // If company object is embedded in the response, use it
      if (company) {
        const addressParts: string[] = [];
        if (company.headOfficeAddress) addressParts.push(company.headOfficeAddress);
        if (company.projectName) addressParts.push(company.projectName);
        if (company.taxSoi) addressParts.push(`ซอย ${company.taxSoi}`);
        if (company.taxRoad) addressParts.push(`ถนน ${company.taxRoad}`);
        if (company.taxSubdistrict) addressParts.push(`ตำบล ${company.taxSubdistrict}`);
        if (company.taxDistrict) addressParts.push(`อำเภอ ${company.taxDistrict}`);
        if (company.taxProvince) addressParts.push(`จังหวัด ${company.taxProvince}`);
        if (company.postalCode) addressParts.push(company.postalCode);

        return {
          name: company.organizationName || '-',
          address: addressParts.length > 0 ? addressParts.join(' ') : '-',
          taxId: company.taxId || '',
          // ชื่อองค์กรอยู่ในช่อง "ชื่อ" แล้ว จึงต้องระบุชื่อสมาชิกไว้ด้วย
          serviceRecipient: this.getServiceRecipientName(apiData)
        };
      }

      // Company not embedded (only companyId returned) — fall back to individual info
      console.log('⚠️ Company object not in response, falling back to individual info');
    }

    // Use individual user information (default or company-fallback)
    const userInfo = apiData.user?.userInformation;
    console.log('👤 Using individual information:', userInfo);

    return {
      name: this.getIndividualName(apiData),
      address: this.buildFullAddress(userInfo),
      taxId: apiData.user?.cid || ''
    };
  }

  /**
   * ใบเสร็จนี้ออกในนามนิติบุคคลหรือไม่
   *
   * แต่ละตารางเก็บค่านี้คนละช่อง: form/cpd ใช้ typeReceipt ส่วน receipt_log ใช้
   * usedFor เพราะ typeReceipt ของ receipt_log เก็บหมวดของรายการ (เช่น "examList")
   */
  private isCompanyReceipt(apiData: any): boolean {
    return [apiData?.usedFor, apiData?.typeReceipt, apiData?.receiptType].some(
      (value) => typeof value === 'string' && value.toLowerCase() === 'company'
    );
  }

  /**
   * ชื่อ-นามสกุลของเจ้าของคำขอ (ตัวบุคคล) ไม่ว่าจะออกใบเสร็จในนามบุคคลหรือนิติบุคคล
   */
  private getServiceRecipientName(apiData: any): string | undefined {
    const name = this.getIndividualName(apiData);
    return name && name !== '-' ? name : undefined;
  }

  private getIndividualName(apiData: any): string {
    const userInfo = apiData.user?.userInformation;
    const firstName = userInfo?.firstName || userInfo?.firstNameThai || apiData.user?.firstName || '';
    const lastName = userInfo?.lastName || userInfo?.lastNameThai || apiData.user?.lastName || '';
    const prefix = userInfo?.titleName || '';

    return `${prefix}${prefix ? ' ' : ''}${firstName} ${lastName}`.replace(/\s+/g, ' ').trim() || '-';
  }

  /**
   * Map API response to SlipData format
   */
  mapApiResponseToSlipData(apiData: any): SlipData {
    console.log('📋 Mapping API data - codeReceipt:', apiData.codeReceipt);

    let itemDescription: string;
    let amount: number;
    let paymentType: string;
    let paymentMethod: string;
    let notes: string;
    let dateString: string;

    // Get payer information based on receiptType (company or individual)
    const payerInfo = this.getPayerInfo(apiData);

    // Map data based on tab type
    switch (this.tabType) {
      case 'member-card':
        itemDescription = `ค่าทำบัตรสมาชิก - ${apiData.membershipType || 'ประเภทสมาชิก'}`;
        amount = parseFloat(apiData.feeAmount || apiData.amount) || 0;
        paymentType = 'ค่าทำบัตรสมาชิก';
        paymentMethod = apiData.paymentStatus || 'โอนเงิน';
        notes = apiData.remarks || '';
        dateString = apiData.transferDateTime || this.formatDateFromApi(apiData.createdAt || apiData.paymentDate);
        break;

      case 'renew-member':
        itemDescription = 'ค่าต่ออายุสมาชิก';
        amount = parseFloat(apiData.feeAmount || apiData.amount) || 0;
        paymentType = 'ค่าต่ออายุสมาชิก';
        paymentMethod = apiData.paymentStatus || 'โอนเงิน';
        notes = apiData.remarks || '';
        dateString = apiData.transferDateTime || this.formatDateFromApi(apiData.createdAt || apiData.paymentDate);
        break;

      case 'register-appraiser':
        itemDescription = 'ค่าขึ้นทะเบียนผู้ประเมิน';
        amount = parseFloat(apiData.feeAmount || apiData.amount) || 0;
        paymentType = 'ค่าขึ้นทะเบียนผู้ประเมิน';
        paymentMethod = apiData.paymentStatus || 'โอนเงิน';
        notes = apiData.remarks || '';
        dateString = apiData.transferDateTime || this.formatDateFromApi(apiData.createdAt || apiData.paymentDate);
        break;

      case 'appraiser-renewal':
      case 'renew-appraiser':
        itemDescription = 'ค่าต่ออายุบัตรผู้ประเมิน';
        amount = parseFloat(apiData.feeAmount || apiData.amount) || 0;
        paymentType = 'ค่าต่ออายุบัตรผู้ประเมิน';
        paymentMethod = apiData.paymentStatus || 'โอนเงิน';
        notes = apiData.remarks || '';
        dateString = apiData.transferDateTime || this.formatDateFromApi(apiData.createdAt || apiData.paymentDate);
        break;

      case 'training':
        itemDescription = apiData.course?.title || 'ค่าอบรมสัมมนา/คอร์สเรียน';
        amount = parseFloat(apiData.amount || apiData.course?.price) || 0;
        paymentType = 'ค่าอบรม/สัมมนา';
        paymentMethod = apiData.cpdPaymentStatus?.name || apiData.paymentStatus?.name || 'โอนเงิน';
        notes = apiData.notes || '';
        dateString = apiData.transferDateTime || this.formatDateFromApi(apiData.updatedAt || apiData.createdAt || apiData.enrolledAt);
        break;

      case 'exam':
        itemDescription = apiData.sessionTitle || apiData.exam?.examTitle || 'ค่าสอบประเมินผล';
        amount = parseFloat(apiData.amount || apiData.exam?.examFee) || 0;
        paymentType = 'ค่าสอบ';
        paymentMethod = apiData.examListPaymentStatus?.name || apiData.paymentStatus?.name || 'โอนเงิน';
        notes = apiData.notes || '';
        dateString = apiData.transferDateTime || this.formatDateFromApi(apiData.updatedAt || apiData.createdAt || apiData.enrolledAt);
        break;

      default:
        itemDescription = apiData.course?.title || apiData.exam?.title || 'รายการชำระเงิน';
        amount = parseFloat(apiData.amount) || 0;
        paymentType = 'รายการชำระเงิน';
        paymentMethod = apiData.paymentStatus?.name || 'โอนเงิน';
        notes = apiData.notes || '';
        dateString = apiData.transferDateTime || this.formatDateFromApi(apiData.paymentDate || apiData.updatedAt || apiData.createdAt);
        break;
    }

    // Calculate VAT backwards: total already includes 7% VAT
    // Formula: subtotal = total / 1.07, vat = total - subtotal
    const subtotal = amount / 1.07;
    const vat = amount - subtotal;

    return {
      receiptNumber: apiData.codeReceipt || '-',
      date: this.stripTime(dateString),
      paymentType: paymentType,
      items: [
        {
          no: 1,
          description: itemDescription,
          quantity: 1,
          pricePerUnit: subtotal,
          amount: subtotal
        }
      ],
      subtotal: subtotal,
      vat: vat,
      total: amount,
      paymentMethod: paymentMethod,
      payer: {
        name: payerInfo.name,
        address: payerInfo.address,
        taxId: payerInfo.taxId,
        serviceRecipient: payerInfo.serviceRecipient
      },
      notes: notes
    };
  }

  /**
   * Strip the time portion ("เวลา HH:mm น." or "HH:mm") from a date string so
   * the receipt shows the date only. Handles the pre-formatted Thai
   * transferDateTime strings as well as ISO/locale strings with a time tail.
   */
  private stripTime(value: string | undefined): string {
    if (!value) return '-';
    let result = value;
    // Remove Thai "เวลา ... น." suffix (with or without trailing น.)
    result = result.replace(/\s*เวลา\s*[\d:.]+\s*(น\.?)?\s*$/u, '');
    // Remove a trailing standalone HH:mm[:ss] time
    result = result.replace(/[\s,]+\d{1,2}:\d{2}(:\d{2})?\s*$/u, '');
    return result.trim() || '-';
  }

  /**
   * Format a number with thousand separators and 2 decimals (e.g. 2000 -> "2,000.00").
   */
  private formatMoney(value: number): string {
    return (value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Format date from API ISO string to Thai locale string
   */
  private formatDateFromApi(dateString: string | undefined): string {
    if (!dateString) {
      return '-';
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }

      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  }

  /**
   * Load sample data for preview
   */
  loadSampleData(): void {
    console.log('📝 Loading sample data...');

    this.isLoading = false;
    this.error = null;
    this.isSampleData = true;

    this.slipData = {
      receiptNumber: 'FA 68/000001',
      date: '2 January 2568',
      paymentType: 'ค่าอบรม/สัมมนา',
      items: [
        {
          no: 1,
          description: 'ค่าอบรมสัมมนา',
          quantity: 1,
          pricePerUnit: 500.00,
          amount: 500.00
        }
      ],
      subtotal: 500.00,
      vat: 35.00,
      total: 535.00,
      paymentMethod: 'โอนผ่านบัญชี',
      payer: {
        name: 'ตัวอย่างผู้ชำระเงิน',
        address: '123 ถนนตัวอย่าง แขวงตัวอย่าง เขตตัวอย่าง กรุงเทพมหานคร 10700',
        taxId: '1234567890123'
      },
      notes: 'ใบเสร็จรับเงิน/ใบกำกับภาษี ฉบับนี้จะสมบูรณ์ต่อเมื่อเช็คเงินสดหรือเช็คธนาคารถึงกำหนดเรียบร้อยแล้ว'
    };

    console.log('✅ Sample data loaded:', this.slipData);
  }

  /**
   * Generate PDF receipt
   */
  async generatePDF(withWatermark: boolean = false): Promise<void> {
    if (!this.slipData) {
      alert('ไม่มีข้อมูลสำหรับสร้างใบเสร็จ');
      return;
    }

    try {
      // Create new PDF document (Custom size: 21cm x 20cm)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [210, 200] // width: 21cm (210mm), height: 20cm (200mm)
      });

      // Add Thai fonts
      doc.addFileToVFS('Sarabun-Regular.ttf', SarabunNormal);
      doc.addFont('Sarabun-Regular.ttf', 'Sarabun', 'normal');
      doc.addFileToVFS('Sarabun-Bold.ttf', SarabunBold);
      doc.addFont('Sarabun-Bold.ttf', 'Sarabun', 'bold');

      // Set default font
      doc.setFont('Sarabun', 'normal');

      // Force pure black text so the printed receipt stays legible
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(0, 0, 0);

      // Page dimensions (21cm x 20cm)
      const pageWidth = doc.internal.pageSize.getWidth(); // 210mm
      const pageHeight = doc.internal.pageSize.getHeight(); // 200mm
      const margin = 15; // Reduced margin for better space utilization
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;

      // Add VAT Logo
      try {
        const logoImg = new Image();
        logoImg.src = '/vat-logo.png';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          setTimeout(reject, 3000); // 3 second timeout
        });

        const logoWidth = 25;
        const logoHeight = 25;
        const logoX = (pageWidth - logoWidth) / 2;
        doc.addImage(logoImg, 'PNG', logoX, yPos, logoWidth, logoHeight);
        yPos += logoHeight + 8;
      } catch (error) {
        console.warn('Failed to load logo, continuing without it:', error);
      }

      // Header - VAT Logo and Title (Compact layout)
      doc.setFontSize(14);
      doc.setFont('Sarabun', 'bold');
      doc.text('สมาคมผู้ประเมินค่าทรัพย์สินแห่งประเทศไทย', pageWidth / 2, yPos, { align: 'center' });

      yPos += 5;
      doc.setFontSize(11);
      doc.text('The Valuers Association of Thailand', pageWidth / 2, yPos, { align: 'center' });

      yPos += 5;
      doc.setFontSize(8);
      doc.setFont('Sarabun', 'normal');
      const headerInfo = 'สำนักงานใหญ่ : อาคารสำนักงานที่ดินกรุงเทพมหานคร สาขาบางกอกน้อย ชั้น 3';
      const wrappedHeader = doc.splitTextToSize(headerInfo, contentWidth - 10);
      doc.text(wrappedHeader, pageWidth / 2, yPos, { align: 'center' });

      yPos += (wrappedHeader.length * 3.5);
      const addressLine1 = 'ซอยจรัญสนิทวงศ์ 59 ถนนจรัญสนิทวงศ์ แขวงบางบำหรุ เขตบางพลัด กรุงเทพมหานคร 10700 เลขประจำตัวผู้เสียภาษี 0-9930-00174-97-6';
      const wrappedAddress1 = doc.splitTextToSize(addressLine1, contentWidth - 10);
      doc.text(wrappedAddress1, pageWidth / 2, yPos, { align: 'center' });

      yPos += (wrappedAddress1.length * 3.5);
      const addressLine2 = 'โทรศัพท์ 0-2435-9047, 0-2886-6335 โทรสาร 0-2435-5712-3 มือถือ 089-200-8885 Website : www.vat.or.th E-mail : vat.2012.vat@gmail.com';
      const wrappedAddress2 = doc.splitTextToSize(addressLine2, contentWidth - 10);
      doc.text(wrappedAddress2, pageWidth / 2, yPos, { align: 'center' });

      yPos += (wrappedAddress2.length * 3.5) + 6;
      doc.setFontSize(12);
      doc.setFont('Sarabun', 'bold');
      doc.text('ใบเสร็จรับเงิน/ใบกำกับภาษี', pageWidth / 2, yPos, { align: 'center' });

      yPos += 5;
      doc.setFontSize(9);
      doc.text('Original Receipt/Tax Invoice', pageWidth / 2, yPos, { align: 'center' });

      // Receipt details (Compact spacing)
      yPos += 6;
      doc.setFontSize(10);
      doc.setFont('Sarabun', 'bold');

      // Date and Receipt Number
      doc.text(`วันที่: ${this.slipData.date}`, margin + 3, yPos);
      doc.text(`เลขที่: ${this.slipData.receiptNumber}`, pageWidth - margin - 45, yPos);

      yPos += 5.5;
      doc.text('ได้รับเงินจาก', margin + 3, yPos);
      doc.text(`ประเภท: ${this.slipData.paymentType}`, pageWidth / 2 + 10, yPos);

      yPos += 5.5;
      const payerName = `ชื่อ: ${this.slipData.payer.name}`;
      const wrappedName = doc.splitTextToSize(payerName, contentWidth - 6);
      doc.text(wrappedName, margin + 3, yPos);
      yPos += (wrappedName.length * 4.5);

      const payerAddress = `ที่อยู่: ${this.slipData.payer.address}`;
      const wrappedAddress = doc.splitTextToSize(payerAddress, contentWidth - 6);
      doc.text(wrappedAddress, margin + 3, yPos);
      yPos += (wrappedAddress.length * 4.5);

      // Always show the Tax ID line — it is a required field on a Thai tax invoice
      doc.text(`เลขประจำตัวผู้เสียภาษี: ${this.slipData.payer.taxId || '-'}`, margin + 3, yPos);
      yPos += 5.5;

      // ออกใบเสร็จในนามนิติบุคคล: ช่อง "ชื่อ" เป็นชื่อองค์กร จึงต้องระบุชื่อสมาชิก
      // ที่ใช้บริการไว้ด้วย ไม่อย่างนั้นพิมพ์ออกมาแล้วไม่ทราบว่าเป็นใบเสร็จของใคร
      if (this.slipData.payer.serviceRecipient) {
        const recipientLine = `ผู้รับบริการ: ${this.slipData.payer.serviceRecipient}`;
        const wrappedRecipient = doc.splitTextToSize(recipientLine, contentWidth - 6);
        doc.text(wrappedRecipient, margin + 3, yPos);
        yPos += (wrappedRecipient.length * 4.5) + 1;
      }

      // Items table (Optimized for 21cm width)
      yPos += 4;
      const tableData = this.slipData.items.map(item => [
        item.no.toString(),
        item.description,
        item.quantity.toString(),
        this.formatMoney(item.pricePerUnit),
        this.formatMoney(item.amount)
      ]);

      // Calculate table dimensions to fit within borders
      // Page width = 210mm, with 15mm margins on each side = 180mm available
      const tableMarginLeft = margin + 3;
      const tableMarginRight = margin + 3;
      const availableTableWidth = pageWidth - tableMarginLeft - tableMarginRight;

      // Column widths optimized for 21cm width
      // Total: 12 + 80 + 18 + 30 + 28 = 168mm (fits in 174mm available)
      autoTable(doc, {
        startY: yPos,
        head: [['ลำดับ', 'รายการ', 'จำนวน', 'ราคาต่อหน่วย\n(บาท)', 'จำนวนเงิน\n(บาท)']],
        body: tableData,
        theme: 'grid',
        styles: {
          font: 'Sarabun',
          fontSize: 9,
          fontStyle: 'bold',
          cellPadding: 1.5,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          fontStyle: 'bold'
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          fontSize: 9,
          cellPadding: 2
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 12 },
          1: { halign: 'left', cellWidth: 80 },
          2: { halign: 'center', cellWidth: 18 },
          3: { halign: 'right', cellWidth: 30 },
          4: { halign: 'right', cellWidth: 28 }
        },
        margin: { left: tableMarginLeft, right: tableMarginRight },
        tableWidth: availableTableWidth
      });

      // Get final Y position after table
      yPos = (doc as any).lastAutoTable.finalY + 5;

      // Summary - positioned within table boundaries
      // Calculate positions based on table's actual right edge
      const summaryRightEdge = pageWidth - margin - 6;
      const summaryValueX = summaryRightEdge;
      const summaryLabelX = summaryRightEdge - 58;

      doc.setFontSize(9);
      doc.setFont('Sarabun', 'bold');
      doc.text('จำนวนเงินก่อนภาษีมูลค่าเพิ่ม', summaryLabelX, yPos);
      doc.text(this.formatMoney(this.slipData.subtotal), summaryValueX, yPos, { align: 'right' });

      yPos += 4.5;
      doc.text('ภาษีมูลค่าเพิ่ม', summaryLabelX, yPos);
      doc.text(this.formatMoney(this.slipData.vat), summaryValueX, yPos, { align: 'right' });

      yPos += 4.5;
      doc.text('เงินรวมภาษีมูลค่าเพิ่ม', summaryLabelX, yPos);
      doc.text(this.formatMoney(this.slipData.total), summaryValueX, yPos, { align: 'right' });

      // Signature section.
      // เดิมตรึงตำแหน่งจากขอบล่างอย่างเดียว พอเนื้อหาด้านบนยาวขึ้น (รายการหลักสูตร
      // ที่ตัดหลายบรรทัด หรือบรรทัด "ผู้รับบริการ" ของใบเสร็จนิติบุคคล) ส่วนสรุปยอด
      // จะไหลลงมาทับหัวข้อลายเซ็น จึงต้องเลื่อนบล็อกลายเซ็นตามเนื้อหาด้วย
      // และถ้าเลื่อนแล้วยังไม่พอ ให้ขึ้นหน้าใหม่แทนการพิมพ์ทับกัน
      const signatureBlockHeight = 23.5; // หัวข้อ -> ลายเซ็น -> เส้น -> ชื่อในวงเล็บ
      const signatureGap = 6; // ระยะห่างขั้นต่ำจากยอดสรุปถึงหัวข้อลายเซ็น
      const bottomAllowance = 10; // ขอบล่างที่กันไว้ให้บล็อกลายเซ็น
      const preferredSignatureY = pageHeight - margin - 32;
      const lowestSignatureY = pageHeight - bottomAllowance - signatureBlockHeight;

      let signatureY = Math.max(preferredSignatureY, yPos + signatureGap);
      if (signatureY > lowestSignatureY) {
        doc.addPage();
        signatureY = margin + 10;
      }

      doc.setFontSize(9);
      doc.setFont('Sarabun', 'bold');
      doc.setTextColor(0, 0, 0);

      const leftSignX = margin + 45;
      const rightSignX = pageWidth - margin - 45;

      // Title labels
      doc.text('ผู้จัดการสมาคม', leftSignX, signatureY, { align: 'center' });
      doc.text('เจ้าหน้าที่การเงิน', rightSignX, signatureY, { align: 'center' });

      // Add signature images (scaled to fit the box without distorting them)
      try {
        const signatureBoxWidth = 35;
        const signatureBoxHeight = 17;
        const signatureBoxBottom = signatureY + 19;

        const managerImg = await this.loadImage('/assets/images/sign-manager.png');
        const financeImg = await this.loadImage('/assets/images/sign-account.png');

        this.drawSignature(doc, managerImg, leftSignX, signatureBoxBottom, signatureBoxWidth, signatureBoxHeight);
        this.drawSignature(doc, financeImg, rightSignX, signatureBoxBottom, signatureBoxWidth, signatureBoxHeight);
      } catch (error) {
        console.warn('Failed to load signature images:', error);
      }

      // Signature lines
      const lineY = signatureY + 20;
      doc.line(margin + 15, lineY, margin + 75, lineY);
      doc.line(pageWidth - margin - 75, lineY, pageWidth - margin - 15, lineY);

      // Names in parentheses
      const nameY = lineY + 3.5;
      doc.text('(นางจันทราทิพย์  พานาดา)', leftSignX, nameY, { align: 'center' });
      doc.text('(นางสาวอุมากร  เงาประทีป)', rightSignX, nameY, { align: 'center' });

      // Add watermark AFTER all content is rendered (so it appears on top)
      if (withWatermark) {
        const watermarkText = this.rawApiData?.type === 'canceled' ? 'ยกเลิกใบเสร็จ' : 'สำเนา';
        const pageCount = doc.getNumberOfPages();
        for (let pageNo = 1; pageNo <= pageCount; pageNo++) {
          doc.setPage(pageNo);
          this.addWatermark(doc, pageWidth, pageHeight, watermarkText);
        }
      }

      // Save PDF
      const filePrefix = withWatermark ? 'copy_receipt' : 'receipt';
      const fileName = `${filePrefix}_${this.slipData.receiptNumber.replace(/\s/g, '_')}.pdf`;
      doc.save(fileName);

      const successMessage = withWatermark ? 'สร้างใบเสร็จสำเนาสำเร็จ' : 'สร้างใบเสร็จสำเร็จ';
      alert(successMessage);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('เกิดข้อผิดพลาดในการสร้างใบเสร็จ');
    }
  }

  /**
   * Go back to previous page
   */
  /** Reveal the cancel-reason form. */
  openCancelForm(): void {
    this.showCancelForm = true;
    this.cancelReasonInput = '';
    this.actionError = null;
    this.actionSuccess = null;
  }

  /** Hide the cancel-reason form without submitting. */
  closeCancelForm(): void {
    this.showCancelForm = false;
    this.cancelReasonInput = '';
    this.actionError = null;
  }

  cancelReceipt(): void {
    const reason = (this.cancelReasonInput || '').trim();
    if (!reason) {
      this.actionError = 'กรุณากรอกเหตุผลในการยกเลิกใบเสร็จ';
      return;
    }

    // ใบเสร็จการสมัครสอบ: ยกเลิกโดยเปลี่ยนสถานะการชำระเงินของใบสมัครเป็น "ยกเลิกการชำระ"
    if (this.tabType === 'exam') {
      this.cancelExamReceipt(reason);
      return;
    }

    const base = this.getFormApiBase();
    if (!base || !this.paymentId) return;
    this.isActionLoading = true;
    this.actionError = null;
    this.actionSuccess = null;
    this.http.patch(`${environment.apiUrl}/${base}/${this.paymentId}`, {
      paymentStatus: 'ยกเลิกการชำระ',
      cancelReason: reason,
    }).subscribe({
      next: () => {
        this.isActionLoading = false;
        this.showCancelForm = false;
        // กลับไปหน้าที่กดเข้ามา (เช่น "ตรวจสอบการชำระเงิน") แทนการเด้งไปหน้าบัญชีเสมอ
        this.goBack();
      },
      error: (err) => {
        this.isActionLoading = false;
        this.actionError = 'ยกเลิกไม่สำเร็จ: ' + (err.error?.message || err.message);
      }
    });
  }

  reissueReceipt(): void {
    if (!confirm('ยืนยันการออกใบเสร็จใหม่?\n\nระบบจะสร้างรหัสใบเสร็จใหม่ให้อัตโนมัติ')) return;

    // ใบเสร็จการสมัครสอบ: ออกใหม่โดยตั้งสถานะการชำระเงินกลับเป็น "ชำระเงินเสร็จสิ้น"
    if (this.tabType === 'exam') {
      this.reissueExamReceipt();
      return;
    }

    const base = this.getFormApiBase();
    if (!base || !this.paymentId) return;
    this.isActionLoading = true;
    this.actionError = null;
    this.actionSuccess = null;
    this.http.get<{ code: string }>(`${environment.apiUrl}/${base}/next-receipt-code`).subscribe({
      next: ({ code }) => {
        const typeReceipt = this.rawApiData?.typeReceipt;
        const body: any = { paymentStatus: 'ชำระเงินเสร็จสิ้น', codeReceipt: code };
        if (typeReceipt) body.typeReceipt = typeReceipt;
        this.http.patch(`${environment.apiUrl}/${base}/${this.paymentId}`, body).subscribe({
          next: () => {
            this.isActionLoading = false;
            this.actionSuccess = `ออกใบเสร็จใหม่สำเร็จ รหัส: ${code}`;
            this.loadPaymentData(this.paymentId!);
          },
          error: (err) => {
            this.isActionLoading = false;
            this.actionError = 'ออกใบเสร็จไม่สำเร็จ: ' + (err.error?.message || err.message);
          }
        });
      },
      error: (err) => {
        this.isActionLoading = false;
        this.actionError = 'ไม่สามารถสร้างรหัสใบเสร็จได้: ' + (err.error?.message || err.message);
      }
    });
  }

  /**
   * สมัครสอบ: ยกเลิกใบเสร็จ (เปลี่ยนสถานะการชำระเงินเป็น "ยกเลิกการชำระ" = 4)
   * backend จะล้างรหัสใบเสร็จและเปลี่ยน receipt log เป็น canceled ให้เอง
   */
  private cancelExamReceipt(reason: string): void {
    const examListId = this.examListId;
    if (!examListId) {
      this.actionError = 'ไม่พบข้อมูลใบสมัครสอบของใบเสร็จนี้';
      return;
    }

    this.isActionLoading = true;
    this.actionError = null;
    this.actionSuccess = null;

    this.http.patch(`${environment.apiUrl}/exam-lists/${examListId}`, {
      examListPaymentStatusId: 4,
    }).subscribe({
      next: () => {
        const logId = this.receiptLogData?.id || this.rawApiData?.id;

        // บันทึกเหตุผลการยกเลิกไว้ที่ receipt log (ถ้าบันทึกไม่ได้ก็ยังถือว่ายกเลิกสำเร็จ)
        const finish = () => {
          this.ngZone.run(() => {
            this.isActionLoading = false;
            this.showCancelForm = false;
            this.cancelReasonInput = '';
            this.actionSuccess = 'ยกเลิกใบเสร็จเรียบร้อยแล้ว';
            if (this.paymentId) {
              this.loadPaymentData(this.paymentId);
            }
          });
        };

        if (logId) {
          this.http.patch(`${environment.apiUrl}/receipt-log/${logId}`, { cancelReason: reason })
            .subscribe({ next: finish, error: finish });
        } else {
          finish();
        }
      },
      error: (err) => {
        this.ngZone.run(() => {
          this.isActionLoading = false;
          this.actionError = 'ยกเลิกไม่สำเร็จ: ' + (err.error?.message || err.message);
        });
      }
    });
  }

  /**
   * สมัครสอบ: ออกใบเสร็จใหม่ (ตั้งสถานะการชำระเงินกลับเป็น "ชำระเงินเสร็จสิ้น" = 3)
   * backend จะสร้างรหัสใบเสร็จใหม่และ receipt log ใหม่ให้เอง
   */
  private reissueExamReceipt(): void {
    const examListId = this.examListId;
    if (!examListId) {
      this.actionError = 'ไม่พบข้อมูลใบสมัครสอบของใบเสร็จนี้';
      return;
    }

    this.isActionLoading = true;
    this.actionError = null;
    this.actionSuccess = null;

    this.http.patch(`${environment.apiUrl}/exam-lists/${examListId}`, {
      examListPaymentStatusId: 3,
    }).subscribe({
      next: () => {
        // ดึง receipt log ใบใหม่ที่เพิ่งออกมาแสดงแทนใบเดิม
        this.http.get<{ data: any[] }>(`${environment.apiUrl}/receipt-log/exam-list/${examListId}`).subscribe({
          next: (response) => {
            this.ngZone.run(() => {
              const activeLog = (response?.data || []).find(log => log?.type === 'created');
              this.isActionLoading = false;

              if (!activeLog) {
                this.actionError = 'ออกใบเสร็จใหม่แล้ว แต่ไม่พบข้อมูลใบเสร็จ กรุณารีเฟรชหน้าอีกครั้ง';
                return;
              }

              this.actionSuccess = `ออกใบเสร็จใหม่สำเร็จ รหัส: ${activeLog.codeReceipt || '-'}`;
              this.receiptLogData = null;
              this.receiptLogId = null;
              this.paymentId = activeLog.id;
              this.loadPaymentData(activeLog.id);
            });
          },
          error: (err) => {
            this.ngZone.run(() => {
              this.isActionLoading = false;
              this.actionError = 'ออกใบเสร็จใหม่แล้ว แต่โหลดข้อมูลไม่สำเร็จ: ' + (err.error?.message || err.message);
            });
          }
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          this.isActionLoading = false;
          this.actionError = 'ออกใบเสร็จไม่สำเร็จ: ' + (err.error?.message || err.message);
        });
      }
    });
  }

  /**
   * สมัครสอบ: แปลงรหัสใบสมัคร (exam list) เป็น receipt log ที่ใช้งานอยู่ แล้วโหลดใบเสร็จใหม่
   */
  private retryExamReceiptByExamList(examListId: string): void {
    const url = `${environment.apiUrl}/receipt-log/exam-list/${examListId}`;
    console.log('🔁 Retrying receipt lookup by exam list:', url);

    this.http.get<{ data: any[] }>(url).subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          const activeLog = (response?.data || []).find(log => log?.type === 'created');

          if (!activeLog) {
            this.slipData = null;
            this.isSampleData = false;
            this.error = 'ยังไม่ได้ออกใบเสร็จสำหรับรายการนี้';
            this.isLoading = false;
            this.cdr.detectChanges();
            return;
          }

          this.paymentId = activeLog.id;
          this.loadPaymentData(activeLog.id);
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('❌ Error loading receipt log by exam list:', error);
          this.slipData = null;
          this.isSampleData = false;
          this.error = `ไม่สามารถโหลดข้อมูลใบเสร็จได้: ${error.message}`;
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      }
    });
  }

  goBack(): void {
    // 1) กลับไปหน้าที่กดเข้ามาดูใบเสร็จ (ส่งมาทาง query param returnUrl)
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }

    // 2) ไม่มี returnUrl แต่เข้ามาจากหน้าอื่นในระบบ → ย้อนกลับหน้าก่อนหน้า
    if (this.cameFromInsideApp) {
      this.location.back();
      return;
    }

    // 3) เปิดลิงก์ใบเสร็จตรงๆ → กลับหน้าหลักตามประเภทผู้ใช้
    if (this.router.url.includes('/user/')) {
      this.router.navigate(['/user/dashboard']);
    } else {
      this.router.navigate(['/staff/account-worklist']);
    }
  }

  /**
   * Add watermark to PDF (Optimized for 20cm height)
   */
  /**
   * Load an image and resolve once it is decoded (rejects after a short timeout).
   */
  private loadImage(src: string, timeoutMs: number = 2000): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      const timer = setTimeout(() => reject(new Error(`Timeout loading ${src}`)), timeoutMs);
      img.onload = () => {
        clearTimeout(timer);
        resolve(img);
      };
      img.onerror = () => {
        clearTimeout(timer);
        reject(new Error(`Failed to load ${src}`));
      };
      img.src = src;
    });
  }

  /**
   * Draw a signature centred on centerX and sitting on baselineY, scaled down to fit
   * inside maxWidth x maxHeight while keeping its original aspect ratio.
   */
  private drawSignature(
    doc: jsPDF,
    img: HTMLImageElement,
    centerX: number,
    baselineY: number,
    maxWidth: number,
    maxHeight: number
  ): void {
    const naturalWidth = img.naturalWidth || img.width;
    const naturalHeight = img.naturalHeight || img.height;
    if (!naturalWidth || !naturalHeight) {
      return;
    }

    const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
    const width = naturalWidth * scale;
    const height = naturalHeight * scale;

    // Resample to ~300 dpi for the printed size. The source files are several
    // megapixels; embedding them as-is makes the receipt PDF far too heavy to
    // print or e-mail, and the extra pixels are invisible on paper anyway.
    const dpi = 300;
    const targetPx = Math.round((width / 25.4) * dpi);
    const source: HTMLImageElement | HTMLCanvasElement =
      naturalWidth > targetPx ? this.resampleImage(img, targetPx) : img;

    doc.addImage(source, 'PNG', centerX - (width / 2), baselineY - height, width, height);
  }

  /**
   * Redraw an image into a canvas of the given pixel width, keeping its ratio.
   * Returns the original image if a canvas context is not available.
   */
  private resampleImage(img: HTMLImageElement, targetWidthPx: number): HTMLImageElement | HTMLCanvasElement {
    const naturalWidth = img.naturalWidth || img.width;
    const naturalHeight = img.naturalHeight || img.height;
    const canvas = document.createElement('canvas');
    canvas.width = targetWidthPx;
    canvas.height = Math.round((naturalHeight / naturalWidth) * targetWidthPx);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return img;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    this.deepenInk(ctx, canvas.width, canvas.height);
    return canvas;
  }

  /**
   * Darken the signature strokes so they stay readable on a printed receipt.
   * The scans are light, and the ink washes out further on a laser printer.
   * Only pixels that already carry ink are touched, so the soft edges and the
   * transparent background are preserved.
   */
  private deepenInk(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    try {
      const imageData = ctx.getImageData(0, 0, width, height);
      const px = imageData.data;
      const gamma = 1.5;

      for (let i = 0; i < px.length; i += 4) {
        if (px[i + 3] === 0) continue;
        // Leave near-white pixels (paper, not ink) alone
        if (px[i] > 230 && px[i + 1] > 230 && px[i + 2] > 230) continue;

        px[i] = Math.round(255 * Math.pow(px[i] / 255, gamma));
        px[i + 1] = Math.round(255 * Math.pow(px[i + 1] / 255, gamma));
        px[i + 2] = Math.round(255 * Math.pow(px[i + 2] / 255, gamma));
      }

      ctx.putImageData(imageData, 0, 0);
    } catch (error) {
      // Canvas may be tainted in some hosting setups — keep the original pixels
      console.warn('Could not enhance signature contrast:', error);
    }
  }

  private addWatermark(doc: jsPDF, pageWidth: number, pageHeight: number, text: string = 'สำเนา'): void {
    // Save current state
    doc.saveGraphicsState();

    // Set watermark properties (smaller for 20cm height)
    doc.setFont('Sarabun', 'bold');
    doc.setFontSize(60); // Reduced from 80 to fit better
    doc.setTextColor(200, 200, 200); // Light gray color
    (doc as any).setGState((doc as any).GState({ opacity: 0.3 })); // 30% opacity

    // Calculate center position and rotation
    const centerX = pageWidth / 2;
    const centerY = pageHeight / 2;

    // Rotate and add watermark text
    doc.text(text, centerX, centerY, {
      align: 'center',
      angle: 45 // 45 degree rotation
    });

    // Restore state
    doc.restoreGraphicsState();
  }

  /**
   * Unified print method that handles both original and copy based on user type and receipt status
   */
  async printPDF(): Promise<void> {
    if (!this.slipData) {
      alert('ไม่มีข้อมูลสำหรับพิมพ์ใบเสร็จ');
      return;
    }

    let shouldPrintCopy = false;

    // Logic based on user type
    if (this.userType === 'staff') {
      // Staff always prints copy
      shouldPrintCopy = true;
      console.log('👨‍💼 Staff user - printing copy');
    } else {
      // Regular user logic
      if (this.isOriginalReceipt) {
        // First time - print original
        shouldPrintCopy = false;
        console.log('👤 User - first print (original)');

        // Update originalReceipt status to false after printing
        await this.updateOriginalReceiptStatus();
      } else {
        // Already printed before - print copy
        shouldPrintCopy = true;
        console.log('👤 User - subsequent print (copy)');
      }
    }

    // Generate PDF with or without watermark
    await this.generatePDF(shouldPrintCopy);
  }

  /**
   * Update originalReceipt status to false in the database
   */
  private async updateOriginalReceiptStatus(): Promise<void> {
    if (!this.paymentId || !this.tabType) {
      console.warn('Cannot update receipt status - missing paymentId or tabType');
      return;
    }

    // Determine the correct API endpoint based on tab type
    let apiUrl = '';
    switch (this.tabType) {
      case 'member-card':
        apiUrl = `${environment.apiUrl}/member-card-payments/${this.paymentId}`;
        break;
      case 'renew-member':
        apiUrl = `${environment.apiUrl}/renew-member-payments/${this.paymentId}`;
        break;
      case 'register-appraiser':
        apiUrl = `${environment.apiUrl}/register-appraiser-payments/${this.paymentId}`;
        break;
      case 'renew-appraiser':
        apiUrl = `${environment.apiUrl}/renew-appraiser-payments/${this.paymentId}`;
        break;
      case 'training':
        apiUrl = `${environment.apiUrl}/cpd-hours/${this.paymentId}`;
        break;
      case 'exam': {
        const examListId = this.rawApiData?.examListId;
        if (!examListId) {
          console.warn('Cannot update receipt status - missing examListId');
          return;
        }
        apiUrl = `${environment.apiUrl}/exam-lists/${examListId}`;
        break;
      }
      default:
        console.warn('Unknown tab type for updating receipt status');
        return;
    }

    try {
      // Update originalReceipt status to false
      const updateData = {
        originalReceipt: false
      };

      await this.http.patch(apiUrl, updateData).toPromise();

      // Update local state
      this.isOriginalReceipt = false;
      if (this.rawApiData) {
        this.rawApiData.originalReceipt = false;
        this.rawApiData.orginalReceipt = false;
      }

      console.log('✅ Original receipt status updated to false');
    } catch (error) {
      console.error('❌ Failed to update original receipt status:', error);
      // Don't block the print operation if update fails
    }
  }
}
